import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, Subject, Subscription, defer, forkJoin, of, timer } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  shareReplay,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { ISearchResponse as ISearchResult } from 'src/app/core/models/i-search-response';
import { ExternalDataService } from 'src/app/shared/services/external-data.service';
import { IAutoCompleteData } from '../../interfaces/i-auto-complete-data';
import { IManager } from 'src/app/core/models/i-manager';
import { ManagerService } from 'src/app/shared/services/manager.service';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { AppConfig } from 'src/app/app-config';
import { Fund } from 'src/app/core/models/i-fund';
import {
  ISelectListItem,
  SelectListItem,
} from '../../interfaces/i-select-list-item';
import { ISector, Sector } from 'src/app/core/models/i-sector';
import { AssetClass, IAssetClass } from 'src/app/core/models/i-asset-class';
import { InvestmentTrust } from 'src/app/core/models/i-investment-trust';
import { Share } from 'src/app/core/models/i-share';
import { IManagerResponse } from 'src/app/core/models/i-manager-response';
import { RELATED_MANAGER_FORM_PATHS } from '../related-manager-item/constants/form-paths';
import { ALL_SECTORS_SELECTED } from '../related-manager-item/constants/sectors';
import { RelatedManager } from 'src/app/core/models/i-related-manager';
import { ILinkInsert } from 'src/app/core/dtos/interfaces/i-link-insert';
import { DataLinkType } from 'src/app/core/dtos/enums/data-link-type';
import { DataBlockType } from 'src/app/core/dtos/enums/data-block-type';
import { BodyLinkFormat } from 'src/app/core/dtos/enums/body-link-format';
import {
  IPrivateMarketsFundBodyLinkAttempt,
  PrivateMarketsBodyLinkResult,
} from 'src/app/core/dtos/interfaces/i-private-markets-body-link';
import { ITypedSelectListItem } from '../../interfaces/i-typed-select-list-item';
import { SurtitleTagType } from 'src/app/content-management/shared/enums/surtitle-tag-type';
import { PrivateMarketsFund } from 'src/app/core/models/i-private-markets-fund';
import { IPrivateMarketsFundSearchResponse } from 'src/app/core/models/i-private-markets-fund-search-response';
import {
  ambiguityAlternativeNames,
  confidenceTier,
  entityTypeLabel,
  ISmartLink,
} from 'src/app/core/models/i-smart-link';
import { ISmartLinkRunContent } from 'src/app/core/models/i-smart-link-run-request';
import {
  REJECT_REASONS,
  SmartLinkRejectReason,
} from 'src/app/core/dtos/enums/smart-link-reject-reason';
import { SmartLinkDecisionAction } from 'src/app/core/dtos/enums/smart-link-decision-action';
import {
  dismissedCards,
  ISmartLinkDecisionFailure,
  ISmartLinkRunState,
  linkedItems,
  pendingCards,
  SMART_LINK_ALREADY_DECIDED_MESSAGE,
  SMART_LINK_BODY_ALREADY_PRESENT_SUFFIX,
  SMART_LINK_BODY_NAME_NOT_FOUND_SUFFIX,
  SMART_LINK_BODY_NO_EVIDENCE_SUFFIX,
  SMART_LINK_BODY_UNAVAILABLE_SUFFIX,
  SMART_LINK_ALREADY_RUNNING_HINT,
  SMART_LINK_NOT_REVOKED_MESSAGE,
  SMART_LINK_POLL_DEADLINE_MS,
  SMART_LINK_READ_ONLY_HINT,
  SMART_LINK_READY_HINT,
  SMART_LINK_SAVE_FIRST_HINT,
  SMART_LINK_SAVE_IN_PROGRESS_HINT,
  SMART_LINK_RUN_FAILED_MESSAGE,
  SMART_LINK_RUN_LOST_MESSAGE,
  SMART_LINK_UNAVAILABLE_MESSAGE,
  SMART_LINK_UNDO_ACTION_LABEL,
  SMART_LINK_UNDO_DURATION_MS,
  SmartLinkArticleGateway,
  SmartLinkingStateService,
  SmartLinkRunErrorReason,
  SmartLinkRunStatus,
  weakPendingCards,
} from 'src/app/shared/services/smart-linking-state.service';
import {
  ISmartLinkTrigger,
  SmartLinkTriggersService,
} from 'src/app/shared/services/smart-link-triggers.service';
import {
  ISmartLinkCandidate,
  ISmartLinkMentionFlashTarget,
  ISmartLinkRejection,
  SmartLinkCardComponent,
} from '../smart-link-card/smart-link-card.component';
import { ExpandableSectionBoxComponent } from '../expandable-section-box/expandable-section-box.component';
import {
  ISmartLinkTarget,
  SMART_LINK_TARGETS,
  SmartLinkTargetBox,
  smartLinkTargetKey,
} from './constants/smart-link-targets';

export type RelatedDataView = 'selected' | 'suggested';

// What one accept did. bodyLink is null for every type but a Private Markets fund, which is the
// only one that touches the body at all (ADR-0006) - distinct from 'not-found', which means a
// fund was eligible and no cited mention could be linked.
export interface IAcceptOutcome {
  wasAccepted: boolean;
  bodyLink: PrivateMarketsBodyLinkResult | null;
}

// The three partition a run exactly, so a scope's count is always the number of cards it opens.
export type SmartLinkQueueScope = 'review' | 'linked' | 'dismissed';

export interface ISmartLinkQueueScopeOption {
  key: SmartLinkQueueScope;
  label: string;
  count: number;
}

// The candidates read from one mention, rendered as one card. More than one item means the run
// marked them mutually exclusive readings.
export interface ISmartLinkMentionBlock {
  items: ISmartLink[];
}

export interface ISmartLinkSuggestionGroup {
  label: string;
  items: ISmartLink[];
  // The same items as `items`, bucketed by mention; `items` stays the flat list the counts and
  // bulk scopes come from.
  blocks: ISmartLinkMentionBlock[];
  strongItems: ISmartLink[];
  // A group only ever holds those two tiers — weak items are one flat list across all groups —
  // so this is the group's "everything" scope.
  strongAndPossibleItems: ISmartLink[];
  // Excluded from every bulk scope; surfaced so the menu can say why the counts fall short.
  ambiguousCount: number;
  acceptScopes: ISmartLinkAcceptScope[];
}

// One database's share of a total, for the collapsed preview: a count, not the items, because
// nothing is decided from the preview.
export interface ISmartLinkGroupCount {
  label: string;
  count: number;
}

// The raw bucketing every grouped view starts from, before any view decides what to keep.
interface ISmartLinkTargetGroup {
  target: ISmartLinkTarget;
  items: ISmartLink[];
}

// A bulk-accept scope in an "Accept all" menu, built fresh on each open so the counts cannot go
// stale against decisions made since the last render.
export interface ISmartLinkAcceptScope {
  key: string;
  label: string;
  items: ISmartLink[];
}

// The wait is never rendered as a number, only used to pick one of four running strings, so a 5s
// tick costs 12 change-detection passes across a worst-case run rather than 60.
const SMART_LINK_BUSY_TICK_MS = 5000;
const SMART_LINK_SLOW_SECONDS = 10;
const SMART_LINK_LONG_SECONDS = 25;
const SMART_LINK_NEAR_DEADLINE_SECONDS = 45;
// Derived, not typed twice: the copy can never drift from the deadline it describes.
const SMART_LINK_DEADLINE_SECONDS = SMART_LINK_POLL_DEADLINE_MS / 1000;

export const SMART_LINK_EMPTY_CONTENT_SUMMARY =
  'This content has no text yet, so there is nothing to match.';

// Stable across re-registrations, so this panel can only ever hold one entry in the registry.
export const SMART_LINK_TRIGGER_ID = 'related-data';

// The body is TinyMCE HTML and an empty one is usually <p>&nbsp;</p>, so tags and entities go
// before anything decides whether the journalist actually wrote something.
const hasText = (value: string): boolean =>
  /\S/.test(
    (value || '').replace(/<[^>]*>/g, ' ').replace(/&[a-z]+;|&#\d+;/gi, ' ')
  );

export const hasScannableText = (content: ISmartLinkRunContent): boolean =>
  !!content &&
  (hasText(content.headline) ||
    hasText(content.standfirst) ||
    hasText(content.body));

// Which content the in-flight run was started from. 'restored' is a run adopted on page load,
// whose content this page never saw.
type SmartLinkScanContent = 'restored' | 'empty' | 'text';

@Component({
  selector: 'app-related-data',
  templateUrl: './related-data.component.html',
  styleUrls: ['./related-data.component.scss'],
})
export class RelatedDataComponent implements OnChanges, OnDestroy, OnInit {
  @Input() isExpandedByDefault = false;
  @Input() isExpandable = true;
  @Input() managerListControl: FormArray;
  @Input() fundListControl: FormArray;
  @Input() sectorListControl: FormArray;
  @Input() assetClassListControl: FormArray;
  @Input() investmentTrustListControl: FormArray;
  @Input() shareListControl: FormArray;
  @Input() privateMarketsFundListControl: FormArray;
  @Input() isShowingSurtitles: boolean = false;
  // Article-only: with it off the panel must render exactly as it does for the other forms.
  @Input() isSmartLinkingEnabled = false;
  @Input() isBodyLinkFormatsEnabled = false;
  @Input() articleId: string;
  @Input() smartLinkGateway: SmartLinkArticleGateway;
  @Input() isEditMode = false;

  @Output() handleAddToBody = new EventEmitter<ILinkInsert>();
  @Output() handleAddBlockToBody = new EventEmitter<DataBlockType>();
  @Output() handleAddPrivateMarketsFundBodyLink =
    new EventEmitter<IPrivateMarketsFundBodyLinkAttempt>();
  @Output() handleRemoveLink = new EventEmitter<{
    regex: string;
    newValue: string;
  }>();
  @Output() isLoading = new EventEmitter<boolean>();
  @Output() handleItemSelected = new EventEmitter<ITypedSelectListItem>();
  @Output() handleItemRemoved = new EventEmitter<ITypedSelectListItem>();
  @Output() handleSurtitlePromoted = new EventEmitter<ITypedSelectListItem>();
  @Output() handleFlashMention = new EventEmitter<
    ISmartLinkMentionFlashTarget[]
  >();

  destroy$ = new Subject<boolean>();
  RELATED_MANAGER_FORM_PATHS = RELATED_MANAGER_FORM_PATHS;
  SurtitleTagType = SurtitleTagType;

  private readonly MANAGERS_ROUTE = `/manager/`;
  private readonly FUNDS_ROUTE = `/fund/`;
  private readonly MANAGER_NOT_AVAILABLE_MESSAGE =
    'The selected manager is no longer available and cannot be added.';
  // Manager
  isSearchingManager: boolean = false;
  isLoadingManagerDetails: boolean = false;
  managerSearchFormControl: FormControl = new FormControl('');
  managersResults: ISearchResult[];
  managerSearchResults: IAutoCompleteData[];

  // Fund
  isSearchingFund: boolean = false;
  fundSearchFormControl: FormControl = new FormControl('');
  fundsResults: ISearchResult[];
  fundSearchResults: IAutoCompleteData[];
  fundDisplayList: ISelectListItem[] = [];

  // Sector
  isSearchingSector: boolean = false;
  sectorSearchFormControl: FormControl = new FormControl('');
  sectorsResults: ISearchResult[];
  sectorSearchResults: IAutoCompleteData[];
  sectorDisplayList: ISelectListItem[] = [];

  // Asset class
  isSearchingAssetClass: boolean = false;
  assetClassSearchFormControl: FormControl = new FormControl('');
  assetClassResults: ISearchResult[];
  assetClassSearchResults: IAutoCompleteData[];
  assetClassDisplayList: ISelectListItem[] = [];

  // Investment trust
  isSearchingInvestmentTrust: boolean = false;
  investmentTrustSearchFormControl: FormControl = new FormControl('');
  investmentTrustResults: ISearchResult[];
  investmentTrustSearchResults: IAutoCompleteData[];
  investmentTrustDisplayList: ISelectListItem[] = [];

  // Share
  isSearchingShare: boolean = false;
  shareSearchFormControl: FormControl = new FormControl('');
  shareResults: ISearchResult[];
  shareSearchResults: IAutoCompleteData[];
  shareDisplayList: ISelectListItem[] = [];

  // Private Markets fund
  isSearchingPrivateMarketsFund: boolean = false;
  privateMarketsFundSearchFormControl: FormControl = new FormControl('');
  privateMarketsFundsResults: IPrivateMarketsFundSearchResponse[];
  privateMarketsFundSearchResults: IAutoCompleteData[];
  privateMarketsFundDisplayList: ISelectListItem[] = [];
  privateMarketsFundBodyFormats: { label: string; format?: string }[] = [
    { label: 'Link' },
    { label: 'Card', format: BodyLinkFormat.Card },
    { label: 'Hovercard', format: BodyLinkFormat.Hovercard },
  ];

  isGettingDerivedAssetClassesAndSectors: boolean = false;

  // Smart linking
  activeView: RelatedDataView = 'selected';
  smartLinkStatus: SmartLinkRunStatus = 'idle';
  smartLinkErrorReason: SmartLinkRunErrorReason | null = null;
  pendingSmartLinks: ISmartLink[] = [];
  weakSmartLinks: ISmartLink[] = [];
  dismissedSmartLinks: ISmartLink[] = [];
  linkedSmartLinks: ISmartLink[] = [];
  allSmartLinks: ISmartLink[] = [];
  suggestionGroups: ISmartLinkSuggestionGroup[] = [];
  // Over every undecided suggestion rather than suggestionGroups: those exclude the weak matches,
  // so a breakdown taken from them would not sum to the line it sits under.
  pendingGroupCounts: ISmartLinkGroupCount[] = [];
  // Derived on each state emission, never from the template: an *ngFor over a method's fresh array
  // rebuilds the menu items on every change-detection pass, and mousedown alone triggers one — so
  // the button the press started on is gone by mouseup and no click is ever raised.
  queueAcceptScopes: ISmartLinkAcceptScope[] = [];
  queueAmbiguousCount = 0;
  // Above this the collapsed preview reports a count of databases instead of naming them: five or
  // more need a third wrapped line at panel width.
  readonly MAX_NAMED_SUGGESTION_GROUPS = 4;
  smartLinkPendingCount = 0;
  smartLinkDismissedCount = 0;
  smartLinkLinkedCount = 0;
  smartLinkReviewedCount = 0;
  isSmartLinkEmptyResult = false;
  isSmartLinkAllReviewed = false;
  decisionsInFlight: string[] = [];
  smartLinkScope: SmartLinkQueueScope = 'review';
  // Derived on each state emission for the same reason as queueAcceptScopes.
  smartLinkScopeOptions: ISmartLinkQueueScopeOption[] = [];
  smartLinkedManagerIds: string[] = [];
  // Spoken, not shown: a count changing is not announced, so a decision needs a live region
  // naming what it did. One region for both verdicts — two would compete.
  decisionAnnouncement: string | null = null;
  readonly rejectReasons = REJECT_REASONS;

  // View-only provenance of accepted suggestions, keyed `${box}:${record_id}`.
  private smartLinkedKeys = new Set<string>();
  // Box writes still awaiting their outcome, keyed the same way. Distinct from smartLinkedKeys,
  // which a sync box write also claims before it actually lands: this is what tells a second
  // suggestion resolving to the same record to join the write in flight rather than repeat it.
  private pendingBoxWrites = new Map<
    string,
    { result$: Observable<IAcceptOutcome>; smartLinkIds: Set<string> }
  >();
  // NOTE: PM-165: undecided suggestions whose record is already in its box but whose target
  // keeps suggesting it anyway (Private Markets Fund only — see smart-link-targets.ts). Kept in
  // the queue and marked, rather than hidden, then excluded from the review count and
  // bulk-accept scopes. Rebuilt on every state emission.
  private alreadyTaggedSmartLinkIds = new Set<string>();
  // Set while a user-triggered run is in flight, so its terminal transitions can raise the failure
  // snackbar or undo the view switch. A run restored by activate() arrives unset and stays silent.
  private userRunInFlight = false;
  // Only a run this component triggered leaves 'restored'.
  private smartLinkScanContent: SmartLinkScanContent = 'restored';
  // The text the run was started from, so each card can show its mention in context. Outlives
  // smartLinkScanContent, which resets to 'restored' the moment the run stops being busy — which
  // is exactly when the results need placing in that text. Null until this panel triggers a run.
  smartLinkScanSnapshot: ISmartLinkRunContent | null = null;
  private smartLinkPollStartedAt: number | null = null;
  private smartLinkWaitSeconds = 0;
  private smartLinkBusyTicker: Subscription | null = null;
  private smartLinkUndoAction: Subscription | null = null;

  // What the content status bar's Smart Link button runs. One object, so the identity it
  // registers under is the identity it unregisters with.
  private readonly smartLinkTrigger: ISmartLinkTrigger = {
    isBusy: () => this.isSmartLinkBusy,
    canRun: () => !!this.articleId && this.isEditMode,
    run: () => this.triggerSmartLinkRun({ revealSuggestions: false }),
  };

  @ViewChild(ExpandableSectionBoxComponent)
  private expandableBox: ExpandableSectionBoxComponent;

  @ViewChild('suggestionQueue')
  private suggestionQueue: ElementRef<HTMLElement>;

  // DOM order, so the index of a decided card locates its neighbour for focus handoff.
  @ViewChildren(SmartLinkCardComponent)
  private smartLinkCards: QueryList<SmartLinkCardComponent>;

  constructor(
    public externalDataService: ExternalDataService,
    private appConfig: AppConfig,
    private fb: FormBuilder,
    private managerService: ManagerService,
    private dialogService: DialogService,
    public smartLinkingState: SmartLinkingStateService,
    private _snackBar: MatSnackBar,
    private smartLinkTriggers: SmartLinkTriggersService
  ) {}

  ngOnDestroy(): void {
    this.stopSmartLinkBusyTicker();
    // A gallery instance being destroyed must not tear down a run belonging to an open article.
    if (this.isSmartLinkingEnabled) {
      this.smartLinkTriggers.unregister(
        SMART_LINK_TRIGGER_ID,
        this.smartLinkTrigger
      );
      this.smartLinkingState.deactivate();
    }
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  ngOnInit() {
    this.initManagerSearch();
    this.initFundsSearch();
    this.initSectorsSearch();
    this.initAssetClassesSearch();
    this.initInvestmentTrustSearch();
    this.initSharesSearch();

    if (this.privateMarketsFundListControl) {
      this.initPrivateMarketsFundsSearch();
    }

    if (this.isSmartLinkingEnabled) {
      this.initSmartLinking();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.isSmartLinkingEnabled || !changes.articleId) {
      return;
    }
    if (!this.articleId) {
      this.smartLinkingState.activateUnsaved();
      return;
    }
    this.smartLinkingState.activate(this.articleId);
  }

  resetSearchResults() {
    this.managerSearchResults = [];
    this.managersResults = [];
    this.fundSearchResults = [];
    this.fundsResults = [];
    this.sectorSearchResults = [];
    this.sectorsResults = [];
    this.assetClassSearchResults = [];
    this.assetClassResults = [];
    this.investmentTrustSearchResults = [];
    this.investmentTrustResults = [];
    this.shareSearchResults = [];
    this.shareResults = [];
    this.privateMarketsFundSearchResults = [];
    this.privateMarketsFundsResults = [];
  }

  // MANAGERS
  initManagerSearch() {
    this.managerListControl.valueChanges
      .pipe(switchMap((_) => this._getDerivedAssetClassesAndSectors$()))
      .subscribe();

    this.managerSearchFormControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        debounceTime(500),
        tap(() => {
          this.isSearchingManager = true;
        }),
        switchMap((searchedValue: string) => {
          const getManagersObs$ = searchedValue
            ? this.externalDataService.searchManagers$(searchedValue)
            : of(null);

          return getManagersObs$.pipe(
            finalize(() => {
              this.isSearchingManager = false;
            })
          );
        })
      )
      .subscribe({
        next: (searchResults: ISearchResult[]) => {
          if (searchResults) {
            this.managersResults = searchResults;
            this.managerSearchResults = searchResults.map((p) => {
              return { displayName: p.title, id: p.id.toString() };
            });
          }
        },
      });
  }

  addManager($event: ISelectListItem) {
    this.addManager$($event).pipe(takeUntil(this.destroy$)).subscribe();
    this.resetSearchResults();
  }

  // The manager box is the only one whose add is asynchronous, and smart linking needs the outcome
  // before it records an accept — hence a variant that emits whether the manager is in the box.
  private addManager$($event: ISelectListItem): Observable<boolean> {
    if (
      this.managerListControl.value.filter(
        (x) => x[RELATED_MANAGER_FORM_PATHS.SELECTED_MANAGER()]?.id == $event.id
      ).length > 0
    ) {
      return of(true);
    }
    this.isLoadingManagerDetails = true;
    return this.managerService.getManagerById$(parseInt($event.id, 10)).pipe(
      map((manager) => {
        this.isLoadingManagerDetails = false;
        if (!manager) {
          this.dialogService.openErrorDialog({
            message: this.MANAGER_NOT_AVAILABLE_MESSAGE,
          });
          return false;
        }
        this.managerListControl.push(this._generateManagerFormGroup(manager));
        this.onItemSelected(
          {
            displayName: $event.displayName,
            id: $event.id.toString(),
          },
          SurtitleTagType.Manager
        );
        return true;
      }),
      catchError((error) => {
        this.isLoadingManagerDetails = false;
        this.dialogService.openErrorDialog({
          message:
            error?.status === 404
              ? this.MANAGER_NOT_AVAILABLE_MESSAGE
              : 'An error has occurred whilst loading the manager. Please try again.',
        });
        return of(false);
      })
    );
  }

  // revokeSmartLink is false only when removeSmartLinkedRecord already owns revoking the
  // decision for this call, so a manual delete-chip click (the default) is the only path that
  // revokes here.
  removeManager(managerToRemove: IManager, revokeSmartLink: boolean = true) {
    let indexToRemove = this.managerListControl.value.findIndex(
      (x) =>
        x[RELATED_MANAGER_FORM_PATHS.SELECTED_MANAGER()].id ==
        managerToRemove.id
    );
    if (indexToRemove > -1) {
      this.handleRemoveLink.emit({
        regex:
          '(<a[^>]*data-manager-id="' +
          managerToRemove.id +
          '"[^>]*>(.*?)</[a|A]>)',
        newValue: '$2',
      });
      this.managerListControl.removeAt(indexToRemove);
      this.onItemRemoved(
        {
          displayName:
            managerToRemove.firstName + ' ' + managerToRemove.lastName,
          id: managerToRemove.id.toString(),
        },
        SurtitleTagType.Manager
      );
      if (revokeSmartLink) {
        this.revokeLinkedSmartLink('manager', managerToRemove.id);
      }
    }
  }

  managerInfoClick($event: IManager) {
    window.open(
      this.appConfig.amrUrl + this.MANAGERS_ROUTE + $event.id,
      '_blank'
    );
  }

  private _getDerivedAssetClassesAndSectors$() {
    let relatedManagers = this.managerListControl.controls.map((formGroup) => {
      let selectedSectorControl = formGroup.get(
        RELATED_MANAGER_FORM_PATHS.SELECTED_SECTOR()
      );
      let managerControl = formGroup.get(
        RELATED_MANAGER_FORM_PATHS.SELECTED_MANAGER()
      );
      return new RelatedManager(
        managerControl.value,
        selectedSectorControl.value
      );
    });

    this.isGettingDerivedAssetClassesAndSectors = true;
    this.isLoading.emit(true);

    return this.externalDataService
      .derivedAssetClassesAndSectors$(
        this.fundListControl.value.map((p) => p.id),
        this.managerService.mapRelatedManagersToManager(relatedManagers),
        this.sectorListControl.value.filter((p) => !p.derived).map((p) => p.id)
      )
      .pipe(
        tap((result) => {
          this._addDerivedAssetClasses(result.assetClasses);
          this._addDerivedSectors(result.sectors);
        }),
        finalize(() => {
          this.isGettingDerivedAssetClassesAndSectors = false;
          this.isLoading.emit(false);
        })
      );
  }

  addManagerToBody(manager: IManager) {
    this.handleAddToBody.emit({
      id: manager.id.toString(),
      name: manager.firstName + ' ' + manager.lastName,
      dataLinkType: DataLinkType.MANAGER,
    });
  }

  private _addDerivedAssetClasses(assetClasses: IAssetClass[]) {
    for (let i = this.assetClassListControl.controls.length - 1; i >= 0; i--) {
      const assetClass = this.assetClassListControl.at(i).value as IAssetClass;
      if (assetClass.derived) {
        this.assetClassListControl.removeAt(i);
        this.onItemRemoved(
          { displayName: assetClass.name, id: assetClass.id.toString() },
          SurtitleTagType.AssetClass
        );
      }
    }
    assetClasses.forEach((toAdd) => {
      const isAdded = this.assetClassListControl.value.find(
        (p) => p.id === toAdd.id
      );
      if (!isAdded) {
        const assetClass = new AssetClass(toAdd.id, toAdd.name, true);
        this.assetClassListControl.push(new FormControl(assetClass));
        this.onItemSelected(
          { displayName: assetClass.name, id: assetClass.id.toString() },
          SurtitleTagType.AssetClass
        );
      }
    });
  }

  private _addDerivedSectors(sectors: ISector[]) {
    for (let i = this.sectorListControl.controls.length - 1; i >= 0; i--) {
      const sector = this.sectorListControl.at(i).value as ISector;
      if (sector.derived) {
        this.sectorListControl.removeAt(i);
        this.onItemRemoved(
          { displayName: sector.name, id: sector.id.toString() },
          SurtitleTagType.Sector
        );
      }
    }
    sectors.forEach((toAdd) => {
      const isAdded = this.sectorListControl.value.find(
        (p) => p.id === toAdd.id
      );
      if (!isAdded) {
        const sector = new Sector(toAdd.id, toAdd.name, true);
        this.sectorListControl.push(new FormControl(sector));
        this.onItemSelected(
          { displayName: sector.name, id: sector.id.toString() },
          SurtitleTagType.Sector
        );
      }
    });
  }

  private _generateManagerFormGroup(manager: IManagerResponse): FormGroup {
    return this.fb.group({
      [RELATED_MANAGER_FORM_PATHS.SELECTED_MANAGER()]: manager,
      [RELATED_MANAGER_FORM_PATHS.SELECTED_SECTOR()]: ALL_SECTORS_SELECTED,
    });
  }

  getSelectedManagers(): IManager[] {
    return this.managerListControl.controls.map(
      (formGroup) =>
        formGroup.get([RELATED_MANAGER_FORM_PATHS.SELECTED_MANAGER()]).value
    );
  }

  handleManagerRating(rating: string): boolean {
    return rating !== undefined && rating.toLowerCase() !== 'notrated';
  }

  // FUNDS
  initFundsSearch() {
    this.fundDisplayList = this.markSmartLinked(
      this.fundListControl.value.map((y) => new SelectListItem(y.id, y.name)),
      'fund'
    );

    this.fundListControl.valueChanges
      .pipe(
        switchMap((x) => {
          this.fundDisplayList = this.markSmartLinked(
            x.map((y) => new SelectListItem(y.id, y.name)),
            'fund'
          );
          return this._getDerivedAssetClassesAndSectors$();
        })
      )
      .subscribe();

    this.fundSearchFormControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        debounceTime(500),
        tap(() => {
          this.isSearchingFund = true;
        }),
        switchMap((searchedValue: string) => {
          const getFundsObs$ = searchedValue
            ? this.externalDataService.searchFunds$(searchedValue)
            : of(null);

          return getFundsObs$.pipe(
            finalize(() => {
              this.isSearchingFund = false;
            })
          );
        })
      )
      .subscribe({
        next: (searchResults: ISearchResult[]) => {
          if (searchResults) {
            this.fundsResults = searchResults;
            this.fundSearchResults = searchResults.map((p) => {
              return { displayName: p.title, id: p.id.toString() };
            });
          }
        },
      });
  }

  addFund($event: ISelectListItem) {
    if (
      this.fundListControl.value.filter((x) => x.id == $event.id).length === 0
    ) {
      const fund = new Fund(parseInt($event.id), $event.displayName);
      this.fundListControl.push(new FormControl(fund));
      this.onItemSelected($event, SurtitleTagType.Fund);
    }
    this.resetSearchResults();
  }

  // Returns whether the record was actually in the box, so smart linking can tell a real unlink
  // from a no-op.
  removeFund(
    $event: ISelectListItem,
    revokeSmartLink: boolean = true
  ): boolean {
    let fundToRemove = this.fundListControl.value.findIndex(
      (x) => x.id == $event.id
    );
    if (fundToRemove > -1) {
      this.handleRemoveLink.emit({
        regex: '(<a[^>]*data-fund-id="' + $event.id + '"[^>]*>(.*?)</[a|A]>)',
        newValue: '$2',
      });
      this.fundListControl.removeAt(fundToRemove);
      this.onItemRemoved($event, SurtitleTagType.Fund);
      if (revokeSmartLink) {
        this.revokeLinkedSmartLink('fund', $event.id);
      }
      return true;
    }
    return false;
  }

  fundInfoClick($event: ISelectListItem) {
    window.open(this.appConfig.amrUrl + this.FUNDS_ROUTE + $event.id, '_blank');
  }

  addFundToBody($event: ISelectListItem) {
    let fund = this.fundListControl.value.find((x) => x.id == $event.id);

    this.handleAddToBody.emit({
      id: fund.id.toString(),
      name: fund.name,
      dataLinkType: DataLinkType.FUND,
    });
  }

  // SECTOR
  initSectorsSearch() {
    this.sectorDisplayList = this.markSmartLinked(
      this.sectorListControl.value.map(
        (y) => new SelectListItem(y.id, y.name, !y.derived)
      ),
      'sector'
    );

    this.sectorListControl.valueChanges.subscribe({
      next: (x) => {
        this.sectorDisplayList = this.markSmartLinked(
          x.map((y) => new SelectListItem(y.id, y.name, !y.derived)),
          'sector'
        );
      },
    });

    this.sectorSearchFormControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        debounceTime(500),
        tap(() => {
          this.isSearchingSector = true;
        }),
        switchMap((searchedValue: string) => {
          const getSectorsObs$ = searchedValue
            ? this.externalDataService.searchSectors$(searchedValue)
            : of(null);

          return getSectorsObs$.pipe(
            finalize(() => {
              this.isSearchingSector = false;
            })
          );
        })
      )
      .subscribe({
        next: (searchResults: ISearchResult[]) => {
          if (searchResults) {
            this.sectorsResults = searchResults;
            this.sectorSearchResults = searchResults.map((p) => {
              return { displayName: p.title, id: p.id.toString() };
            });
          }
        },
      });
  }

  addSector($event: ISelectListItem) {
    if (
      this.sectorListControl.value.filter((x) => x.id == $event.id).length === 0
    ) {
      const sector = new Sector(parseInt($event.id), $event.displayName, false);
      this.sectorListControl.push(new FormControl(sector));
      this.onItemSelected($event, SurtitleTagType.Sector);
      this._getDerivedAssetClassesAndSectors$().subscribe();
    }
    this.resetSearchResults();
  }

  removeSector(
    $event: ISelectListItem,
    revokeSmartLink: boolean = true
  ): boolean {
    let sectorToRemove = this.sectorListControl.value.findIndex(
      (x) => x.id == $event.id
    );
    if (sectorToRemove > -1) {
      this.sectorListControl.removeAt(sectorToRemove);
      this.onItemRemoved($event, SurtitleTagType.Sector);
      if (revokeSmartLink) {
        this.revokeLinkedSmartLink('sector', $event.id);
      }
      this._getDerivedAssetClassesAndSectors$().subscribe();
      return true;
    }
    return false;
  }

  // ASSET CLASS
  initAssetClassesSearch() {
    this.assetClassDisplayList = this.markSmartLinked(
      this.assetClassListControl.value.map(
        (y) => new SelectListItem(y.id, y.name, !y.derived)
      ),
      'assetClass'
    );

    this.assetClassListControl.valueChanges.subscribe({
      next: (x) => {
        this.assetClassDisplayList = this.markSmartLinked(
          x.map((y) => new SelectListItem(y.id, y.name, !y.derived)),
          'assetClass'
        );
      },
    });

    this.assetClassSearchFormControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        debounceTime(500),
        tap(() => {
          this.isSearchingAssetClass = true;
        }),
        switchMap((searchedValue: string) => {
          const getAssetClassesObs$ = searchedValue
            ? this.externalDataService.searchAssetClasses$(searchedValue)
            : of(null);

          return getAssetClassesObs$.pipe(
            finalize(() => {
              this.isSearchingAssetClass = false;
            })
          );
        })
      )
      .subscribe({
        next: (searchResults: ISearchResult[]) => {
          if (searchResults) {
            this.assetClassResults = searchResults;
            this.assetClassSearchResults = searchResults.map((p) => {
              return { displayName: p.title, id: p.id.toString() };
            });
          }
        },
      });
  }

  addAssetClass($event: ISelectListItem) {
    if (
      this.assetClassListControl.value.filter((x) => x.id == $event.id)
        .length === 0
    ) {
      const assetClass = new AssetClass(
        parseInt($event.id),
        $event.displayName,
        false
      );
      this.assetClassListControl.push(new FormControl(assetClass));
      this.onItemSelected($event, SurtitleTagType.AssetClass);
    }
    this.resetSearchResults();
  }

  removeAssetClass(
    $event: ISelectListItem,
    revokeSmartLink: boolean = true
  ): boolean {
    let assetClassToRemove = this.assetClassListControl.value.findIndex(
      (x) => x.id == $event.id
    );
    if (assetClassToRemove > -1) {
      this.assetClassListControl.removeAt(assetClassToRemove);
      this.onItemRemoved($event, SurtitleTagType.AssetClass);
      if (revokeSmartLink) {
        this.revokeLinkedSmartLink('assetClass', $event.id);
      }
      return true;
    }
    return false;
  }

  // Investment trust
  initInvestmentTrustSearch() {
    this.investmentTrustDisplayList = this.markSmartLinked(
      this.investmentTrustListControl.value.map(
        (y) => new SelectListItem(y.id, y.name)
      ),
      'investmentTrust'
    );

    this.investmentTrustListControl.valueChanges.subscribe({
      next: (x) => {
        this.investmentTrustDisplayList = this.markSmartLinked(
          x.map((y) => new SelectListItem(y.id, y.name)),
          'investmentTrust'
        );
      },
    });

    this.investmentTrustSearchFormControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        debounceTime(500),
        tap(() => {
          this.isSearchingInvestmentTrust = true;
        }),
        switchMap((searchedValue: string) => {
          const getInvestmentTrustsObs$ = searchedValue
            ? this.externalDataService.searchInvestmentTrusts$(searchedValue)
            : of(null);

          return getInvestmentTrustsObs$.pipe(
            finalize(() => {
              this.isSearchingInvestmentTrust = false;
            })
          );
        })
      )
      .subscribe({
        next: (searchResults: ISearchResult[]) => {
          if (searchResults) {
            this.investmentTrustResults = searchResults;
            this.investmentTrustSearchResults = searchResults.map((p) => {
              return { displayName: p.title, id: p.id.toString() };
            });
          }
        },
      });
  }

  addInvestmentTrust($event: ISelectListItem) {
    if (
      this.investmentTrustListControl.value.filter((x) => x.id == $event.id)
        .length === 0
    ) {
      const investmentTrust = new InvestmentTrust(
        parseInt($event.id),
        $event.displayName
      );
      this.investmentTrustListControl.push(new FormControl(investmentTrust));
      this.onItemSelected($event, SurtitleTagType.InvestmentTrust);
    }
    this.resetSearchResults();
  }

  removeInvestmentTrust(
    $event: ISelectListItem,
    revokeSmartLink: boolean = true
  ): boolean {
    let investmentTrustToRemove =
      this.investmentTrustListControl.value.findIndex((x) => x.id == $event.id);
    if (investmentTrustToRemove > -1) {
      this.handleRemoveLink.emit({
        regex:
          '(<a[^>]*data-investment-trust-id="' +
          $event.id +
          '"[^>]*>(.*?)</[a|A]>)',
        newValue: '$2',
      });
      this.investmentTrustListControl.removeAt(investmentTrustToRemove);
      this.onItemRemoved($event, SurtitleTagType.InvestmentTrust);
      if (revokeSmartLink) {
        this.revokeLinkedSmartLink('investmentTrust', $event.id);
      }
      return true;
    }
    return false;
  }

  addInvestmentTrustToBody($event: ISelectListItem) {
    let invTrust = this.investmentTrustListControl.value.find(
      (x) => x.id == $event.id
    );
    this.handleAddToBody.emit({
      id: invTrust.id.toString(),
      name: invTrust.name,
      dataLinkType: DataLinkType.INVESTMENT_TRUST,
    });
  }

  // Shares
  initSharesSearch() {
    this.shareDisplayList = this.shareListControl.value.map((y) => {
      return new SelectListItem(y.id, y.companyName);
    });

    this.shareListControl.valueChanges.subscribe({
      next: (x) => {
        this.shareDisplayList = x.map((y) => {
          return new SelectListItem(y.id, y.companyName);
        });
      },
    });

    this.shareSearchFormControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        debounceTime(500),
        tap(() => {
          this.isSearchingShare = true;
        }),
        switchMap((searchedValue: string) => {
          const getSharesObs$ = searchedValue
            ? this.externalDataService.searchShares$(searchedValue)
            : of(null);

          return getSharesObs$.pipe(
            finalize(() => {
              this.isSearchingShare = false;
            })
          );
        })
      )
      .subscribe({
        next: (searchResults: ISearchResult[]) => {
          if (searchResults) {
            this.shareResults = searchResults;
            this.shareSearchResults = searchResults.map((p) => {
              return { displayName: p.title, id: p.id.toString() };
            });
          }
        },
      });
  }

  addShare($event: ISelectListItem) {
    if (
      this.shareListControl.value.filter((x) => x.id == $event.id).length === 0
    ) {
      this.shareListControl.push(
        new FormControl(new Share(parseInt($event.id), $event.displayName))
      );
      this.onItemSelected($event, SurtitleTagType.Share);
    }
    this.resetSearchResults();
  }

  removeShare($event: ISelectListItem) {
    let shareToRemove = this.shareListControl.value.findIndex(
      (x) => x.id == $event.id
    );
    if (shareToRemove > -1) {
      this.handleRemoveLink.emit({
        regex:
          '(<a[^>]*data-share-instrument-id="' +
          $event.id +
          '"[^>]*>(.*?)</[a|A]>)',
        newValue: '$2',
      });
      this.shareListControl.removeAt(shareToRemove);
      this.onItemRemoved($event, SurtitleTagType.Share);
    }
  }

  addShareToBody($event: ISelectListItem) {
    let share = this.shareListControl.value.find((x) => x.id == $event.id);
    this.handleAddToBody.emit({
      id: share.id.toString(),
      name: share.companyName,
      dataLinkType: DataLinkType.SHARE,
    });
  }

  // PRIVATE MARKETS FUND
  initPrivateMarketsFundsSearch() {
    this.seedPrivateMarketsFundSmartLinkedKeys(
      this.privateMarketsFundListControl.value
    );
    this.privateMarketsFundDisplayList = this.markSmartLinked(
      this.privateMarketsFundListControl.value.map(
        (y) => new SelectListItem(y.id, y.name)
      ),
      'privateMarketsFund'
    );

    this.privateMarketsFundListControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((x) => {
        this.seedPrivateMarketsFundSmartLinkedKeys(x);
        this.privateMarketsFundDisplayList = this.markSmartLinked(
          x.map((y) => new SelectListItem(y.id, y.name)),
          'privateMarketsFund'
        );
      });

    this.privateMarketsFundSearchFormControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        debounceTime(500),
        tap(() => {
          this.isSearchingPrivateMarketsFund = true;
        }),
        switchMap((searchedValue: string) => {
          const getPrivateMarketsFunds$ = searchedValue
            ? this.externalDataService.searchPrivateMarketsFunds$(searchedValue)
            : of(null);

          return getPrivateMarketsFunds$.pipe(
            finalize(() => {
              this.isSearchingPrivateMarketsFund = false;
            })
          );
        })
      )
      .subscribe({
        next: (searchResults: IPrivateMarketsFundSearchResponse[]) => {
          if (searchResults) {
            this.privateMarketsFundsResults = searchResults;
            this.privateMarketsFundSearchResults = searchResults.map((p) => {
              return { displayName: p.title, id: p.id };
            });
          }
        },
      });
  }

  addPrivateMarketsFund(
    $event: ISelectListItem,
    addedViaSmartLink: boolean = false
  ) {
    const existingControl = this.privateMarketsFundListControl.controls.find(
      (control) => control.value.id === $event.id
    );
    if (!existingControl) {
      const privateMarketsFund = new PrivateMarketsFund(
        $event.id,
        $event.displayName,
        new Date().toISOString(),
        addedViaSmartLink
      );
      this.privateMarketsFundListControl.push(
        new FormControl(privateMarketsFund)
      );
      this.resetSearchResults();
      return;
    }

    if (addedViaSmartLink && !existingControl.value.addedViaSmartLink) {
      // NOTE: PM-149: an accepted Smart Link still has to flip an already-present fund's
      // provenance to true so the marker persists; a manual add never downgrades true to false.
      existingControl.setValue({
        ...existingControl.value,
        addedViaSmartLink: true,
      });
    }

    this.resetSearchResults();
  }

  removePrivateMarketsFund(
    $event: ISelectListItem,
    revokeSmartLink: boolean = true
  ): boolean {
    const indexToRemove = this.privateMarketsFundListControl.value.findIndex(
      (x) => x.id === $event.id
    );
    if (indexToRemove > -1) {
      this.handleRemoveLink.emit({
        regex:
          '(<a[^>]*data-private-markets-fund-id="' +
          $event.id +
          '"[^>]*>(.*?)</[a|A]>)',
        newValue: '$2',
      });
      this.privateMarketsFundListControl.removeAt(indexToRemove);
      if (revokeSmartLink) {
        this.revokeLinkedSmartLink('privateMarketsFund', $event.id);
      }
      return true;
    }
    return false;
  }

  // NOTE: null, not [] - search-list picks the format menu over the plain "+" on this input's
  // truthiness, so an empty array would render a menu with no options in it. Returns the field
  // itself rather than a copy so the reference stays stable across change detection.
  get privateMarketsFundBodyFormatOptions(): {
    label: string;
    format?: string;
  }[] {
    return this.isBodyLinkFormatsEnabled
      ? this.privateMarketsFundBodyFormats
      : null;
  }

  addPrivateMarketsFundToBody($event: ISelectListItem, format?: string) {
    const privateMarketsFund = this.privateMarketsFundListControl.value.find(
      (x) => x.id === $event.id
    );
    const insert: ILinkInsert = {
      id: privateMarketsFund.id,
      name: privateMarketsFund.name,
      dataLinkType: DataLinkType.PRIVATE_MARKETS_FUND,
    };
    if (format) {
      insert.format = format as BodyLinkFormat;
    }
    this.handleAddToBody.emit(insert);
  }

  addPrivateMarketsFundsBlockToBody(): void {
    this.handleAddBlockToBody.emit(DataBlockType.PRIVATE_MARKETS_FUNDS);
  }

  onItemSelected(selectedItem: ISelectListItem, tagType: SurtitleTagType) {
    this.handleItemSelected.emit({ tag: selectedItem, tagType: tagType });
  }

  onItemRemoved(selectedItem: ISelectListItem, tagType: SurtitleTagType) {
    this.handleItemRemoved.emit({ tag: selectedItem, tagType: tagType });
  }

  promoteSurtitle(selectedItem: ISelectListItem, tagType: SurtitleTagType) {
    this.handleSurtitlePromoted.emit({ tag: selectedItem, tagType: tagType });
  }

  // SMART LINKING
  onTriggerSmartLink(event: Event): void {
    // The button sits inside mat-expansion-panel-header, which toggles on click.
    event.stopPropagation();
    this.triggerSmartLinkRun({ revealSuggestions: true });
  }

  onPreviewReview(event: Event): void {
    event.stopPropagation();
    this.selectView('suggested');
    this.expandableBox?.openPanel();
  }

  // Pressing a button inside the panel is a request to watch the run, so it opens on Suggested.
  // The status bar starts the same run and leaves the panel exactly as the user left it.
  triggerSmartLinkRun(options: { revealSuggestions: boolean }): void {
    if (this.isSmartLinkDisabled) {
      return;
    }
    this.userRunInFlight = true;
    this.decisionAnnouncement = null;
    if (options.revealSuggestions) {
      this.activeView = 'suggested';
      this.expandableBox?.openPanel();
    }
    this.smartLinkingState.run(this.smartLinkGateway);
  }

  // One expression behind both the disabled attribute and the run guard, so they cannot drift
  // apart. A disabled button still matches CSS :hover but receives no mouse events, which is
  // why the hint hangs on the template wrapper rather than on the button.
  get isSmartLinkDisabled(): boolean {
    return (
      this.isSmartLinkBusy ||
      !this.articleId ||
      !this.isEditMode ||
      this.isSmartLinkGatewaySaving
    );
  }

  get isSmartLinkBusy(): boolean {
    return (
      this.smartLinkStatus === 'triggering' ||
      this.smartLinkStatus === 'running'
    );
  }

  // The parent form's own Save, not a run-triggered save: distinct from isSmartLinkBusy so the
  // status bar's own isSaving-based guard never inherits this panel's wording for it.
  get isSmartLinkGatewaySaving(): boolean {
    return !!this.smartLinkGateway?.isSaving();
  }

  get smartLinkBusyText(): string {
    if (this.smartLinkStatus === 'triggering') {
      return 'Sending your content for scanning…';
    }
    if (this.smartLinkWaitSeconds >= SMART_LINK_NEAR_DEADLINE_SECONDS) {
      return `Still scanning — nearly at the ${SMART_LINK_DEADLINE_SECONDS}-second limit`;
    }
    if (this.smartLinkWaitSeconds >= SMART_LINK_LONG_SECONDS) {
      return 'Still scanning — this one is taking a while';
    }
    if (this.smartLinkWaitSeconds >= SMART_LINK_SLOW_SECONDS) {
      return 'Still scanning your content…';
    }
    return 'Scanning your content…';
  }

  get smartLinkErrorMessage(): string {
    if (this.smartLinkErrorReason === 'run_lost') {
      return SMART_LINK_RUN_LOST_MESSAGE;
    }
    if (this.smartLinkErrorReason === 'run_failed') {
      return SMART_LINK_RUN_FAILED_MESSAGE;
    }
    return SMART_LINK_UNAVAILABLE_MESSAGE;
  }

  // Tier order matters: the wait tiers outrank the content lines, so a restored or empty-content
  // run still reaches the recovery instruction.
  get smartLinkBusyDetail(): string {
    if (this.smartLinkWaitSeconds >= SMART_LINK_NEAR_DEADLINE_SECONDS) {
      return 'If it stops, press Smart Link to run it again.';
    }
    if (this.smartLinkWaitSeconds >= SMART_LINK_LONG_SECONDS) {
      return `We stop waiting at ${SMART_LINK_DEADLINE_SECONDS} seconds; you can run it again after that.`;
    }
    if (this.smartLinkWaitSeconds >= SMART_LINK_SLOW_SECONDS) {
      return `We wait up to ${SMART_LINK_DEADLINE_SECONDS} seconds for a result.`;
    }
    if (this.smartLinkScanContent === 'empty') {
      return SMART_LINK_EMPTY_CONTENT_SUMMARY;
    }
    if (this.smartLinkScanContent === 'restored') {
      return 'This run started before you opened this page — its results appear here when it finishes.';
    }
    return 'This usually takes a few seconds.';
  }

  get smartLinkPreviewBusyText(): string {
    if (this.smartLinkStatus === 'triggering') {
      return 'Sending your content for scanning…';
    }
    if (this.smartLinkWaitSeconds >= SMART_LINK_SLOW_SECONDS) {
      return 'Still scanning your content…';
    }
    return 'Scanning your content…';
  }

  // One string, so the polite region speaks the phase and its detail as one utterance.
  get smartLinkBusyAnnouncement(): string {
    return this.isSmartLinkBusy
      ? `${this.smartLinkBusyText} ${this.smartLinkBusyDetail}`
      : '';
  }

  get smartLinkButtonBusyLabel(): string {
    return 'Scanning…';
  }

  get smartLinkButtonTitle(): string {
    if (!this.isEditMode) {
      return SMART_LINK_READ_ONLY_HINT;
    }
    if (!this.articleId) {
      return SMART_LINK_SAVE_FIRST_HINT;
    }
    if (this.isSmartLinkBusy) {
      return SMART_LINK_ALREADY_RUNNING_HINT;
    }
    if (this.isSmartLinkGatewaySaving) {
      return SMART_LINK_SAVE_IN_PROGRESS_HINT;
    }
    return SMART_LINK_READY_HINT;
  }

  get allReviewedLabel(): string {
    return this.allSmartLinks.length === 1
      ? 'Suggestion reviewed'
      : `All ${this.allSmartLinks.length} reviewed`;
  }

  get smartLinkReviewProgressLabel(): string {
    return `${this.smartLinkReviewedCount} of ${this.allSmartLinks.length} reviewed`;
  }

  // Decided rather than linked: a dismissal is progress too, and a bar that only grew on accepts
  // would sit still through a run the journalist rejected outright.
  get smartLinkReviewedPercent(): number {
    return this.allSmartLinks.length === 0
      ? 0
      : (this.smartLinkReviewedCount / this.allSmartLinks.length) * 100;
  }

  // The all-reviewed segment already states the total, so this reads only part-way through.
  get isSmartLinkReviewProgressShown(): boolean {
    return !this.isSmartLinkAllReviewed && this.allSmartLinks.length > 0;
  }

  // A zero separates an emptied queue from one that was never filled.
  get isSuggestedTabCountShown(): boolean {
    return this.smartLinkPendingCount > 0 || this.isSmartLinkAllReviewed;
  }

  // Present for the whole life of a run that returned something: a bar that materialised on the
  // first decision moved the queue down under a cursor already aimed at a card.
  get isSmartLinkSummaryShown(): boolean {
    return this.allSmartLinks.length > 0;
  }

  // A saved article that has never been scanned. `!articleId` is the save hint's case, and a run
  // aborted by a failed pre-run save also lands back on 'idle' — correctly, since nothing ran.
  get isSmartLinkIdle(): boolean {
    return this.smartLinkStatus === 'idle' && !!this.articleId;
  }

  // onTriggerSmartLink refuses outside edit mode, so the copy must not promise a scan the reader
  // has no way to start.
  get smartLinkIdleDetail(): string {
    return this.isEditMode
      ? 'Run Smart Link to scan this content for managers, funds, sectors and more.'
      : 'Switch to edit mode to run Smart Link.';
  }

  // An accepted record lands in the other view, so without this the only feedback is the card
  // disappearing. Shares are excluded: they have a FormArray but no box in Selected.
  get selectedCount(): number {
    return (
      this.managerListControl.length +
      this.fundListControl.length +
      this.sectorListControl.length +
      this.assetClassListControl.length +
      this.investmentTrustListControl.length +
      (this.privateMarketsFundListControl?.length ?? 0)
    );
  }

  selectView(view: RelatedDataView): void {
    this.activeView = view;
    this.decisionAnnouncement = null;
  }

  // Takes the id rather than the item because a card can hold several candidates, so the template
  // has no loop variable naming which of them was decided.
  onAcceptSuggestion(smartLinkId: string): void {
    const item = this.allSmartLinks.find(
      (candidate) => candidate.smart_link_id === smartLinkId,
    );
    if (!item) {
      return;
    }
    const neighbourId = this.neighbourCardId(smartLinkId);
    this.acceptSuggestion(item)
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ wasAccepted, bodyLink }) => {
        if (!wasAccepted) {
          // addManager$ has already explained itself in a dialog, and the card keeps focus
          // because it stays.
          return;
        }
        this.decisionAnnouncement = `Added ${
          item.display_name
        }${this.bodyLinkSuffix(bodyLink)}`;
        if (this.isReportedBodyMiss(bodyLink)) {
          // NOTE: decisionAnnouncement already speaks this, so the snackbar's own live region
          // stays off (ADR-0009).
          this._snackBar.open(this.decisionAnnouncement, null, {
            duration: 5000,
            politeness: 'off',
          });
        }
        this.focusAfterDecision(neighbourId);
      });
  }

  onRejectSuggestion(rejection: ISmartLinkRejection): void {
    const item = this.allSmartLinks.find(
      (candidate) => candidate.smart_link_id === rejection.id,
    );
    const neighbourId = this.neighbourCardId(rejection.id);
    this.smartLinkingState.reject(rejection.id, rejection.reason);
    this.decisionAnnouncement = `Dismissed ${item?.display_name ?? 'suggestion'}`;
    this.focusAfterDecision(neighbourId);
  }

  onRestoreSuggestion(smartLinkId: string): void {
    const item = this.allSmartLinks.find(
      (candidate) => candidate.smart_link_id === smartLinkId,
    );
    const neighbourId = this.neighbourCardId(smartLinkId);
    this.smartLinkingState.undoReject(smartLinkId);
    this.decisionAnnouncement = `Restored ${
      item?.display_name ?? 'suggestion'
    } to the queue`;
    this.focusAfterDecision(neighbourId);
  }

  // Mirrors the order the template renders cards in, per scope. Read from the data rather than the
  // ViewChildren QueryList: all four scopes share one #suggestionCard template, so that list is not
  // a per-scope ordering and its index cannot be trusted to name a neighbour.
  private renderedCardBlocks(): ISmartLink[][] {
    if (this.smartLinkScope === 'linked') {
      return this.smartLinkSingletons(this.linkedSmartLinks);
    }
    if (this.smartLinkScope === 'dismissed') {
      return this.smartLinkSingletons(this.dismissedSmartLinks);
    }
    return [
      ...this.suggestionGroups.flatMap((group) =>
        group.blocks.map((block) => block.items)
      ),
      ...this.smartLinkSingletons(this.weakSmartLinks),
    ];
  }

  // The card before the one being decided, named rather than numbered: the decided card only
  // leaves when its POST resolves, and by then the positions have moved.
  private neighbourCardId(smartLinkId: string): string | null {
    const blocks = this.renderedCardBlocks();
    const index = blocks.findIndex((items) =>
      items.some((item) => item.smart_link_id === smartLinkId)
    );
    if (index <= 0) {
      return null;
    }
    return blocks[index - 1][0]?.smart_link_id ?? null;
  }

  // The prior card, then the container — never the card that slid into the vacated slot, which
  // hands focus to a decision the user has not read. Waits for the queue to actually change rather
  // than a fixed tick: the decision is recorded over the network.
  private focusAfterDecision(neighbourId: string | null): void {
    if (!neighbourId) {
      this.focusSuggestionQueue();
      return;
    }
    this.smartLinkCards?.changes
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(() => {
        const neighbour = this.smartLinkCards.find((card) =>
          card.holds(neighbourId)
        );
        if (neighbour) {
          neighbour.focus();
          return;
        }
        this.focusSuggestionQueue();
      });
  }

  private focusSuggestionQueue(): void {
    setTimeout(() => this.suggestionQueue?.nativeElement.focus());
  }

  // Carries its own edit-mode and scope gates: the control sits inside the summary bar, which has
  // no wrapper row to hang them on.
  get isQueueAcceptShown(): boolean {
    return (
      this.isEditMode &&
      this.smartLinkScope === 'review' &&
      this.queueAcceptScopes.length > 0
    );
  }

  onAcceptScope(scope: ISmartLinkAcceptScope): void {
    const candidates = this.longestFundNamesFirst(
      this.acceptableItems(scope.items)
    );
    if (candidates.length === 0) {
      return;
    }
    // Per item, because a scope spans boxes: one add that cannot be answered must not take the
    // rest of the batch, the announcement and the undo with it. Each emits a not-accepted
    // outcome instead.
    forkJoin(
      candidates.map((item) =>
        this.acceptSuggestion(item).pipe(map((outcome) => ({ item, outcome })))
      )
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe((results) => {
        const accepted = results.filter(({ outcome }) => outcome.wasAccepted);
        if (accepted.length === 0) {
          this._snackBar.open(SMART_LINK_UNAVAILABLE_MESSAGE, null, {
            duration: 5000,
          });
          return;
        }
        const acceptedItems = accepted.map(({ item }) => item);
        // NOTE: Misses count only eligible Private Markets funds, never the whole batch, and fold
        // into the Undo snackbar rather than opening a second one that would dismiss it (ADR-0009).
        const linked = accepted.filter(
          ({ outcome }) => outcome.bodyLink === 'linked'
        ).length;
        const missed = accepted.filter(
          ({ outcome }) =>
            outcome.bodyLink === 'not-found' ||
            outcome.bodyLink === 'unavailable'
        ).length;
        this.decisionAnnouncement =
          acceptedItems.length === 1
            ? `Added ${acceptedItems[0].display_name}${this.bodyLinkSuffix(
                accepted[0].outcome.bodyLink
              )}`
            : this.bulkAcceptAnnouncement(acceptedItems.length, linked, missed);
        this.openUndoSnackBar(this.decisionAnnouncement, () =>
          this.undoBulkAccept(acceptedItems)
        );
        // A bulk accept empties whole stretches of the queue, so there is no neighbouring card
        // to hand focus to.
        this.focusSuggestionQueue();
      });
  }

  acceptScopePreview(scope: ISmartLinkAcceptScope): string {
    return `Accepts: ${scope.items
      .map((item) => item.display_name)
      .join(', ')}`;
  }

  // The same reversal as a card's own Unlink, so the snackbar and the Linked scope cannot disagree
  // about what an undone accept leaves behind. Records come out of their boxes before the
  // decisions are revoked, as undoAccept requires.
  // NOTE: PM-153: `removed` is deliberately every item in scope, not just the ones whose box
  // write this call actually undid — two suggestions in one bulk accept can resolve to the same
  // record (see acceptSuggestion's pendingBoxWrites dedupe), so undoing the first already empties
  // the box before the second's removeSmartLinkedRecord call runs. Filtering that second one out
  // would strand its decision exactly like the bug this fixes, so it still counts in both the
  // undoAccept calls below and the announcement.
  private undoBulkAccept(items: ISmartLink[]): void {
    const removed = items.filter((item) => this.removeSmartLinkedRecord(item));
    if (removed.length === 0) {
      return;
    }
    removed.forEach((item) =>
      this.smartLinkingState.undoAccept(item.smart_link_id),
    );
    this.decisionAnnouncement =
      removed.length === 1
        ? `Unlinked ${removed[0].display_name} and returned it to the queue`
        : `Unlinked ${removed.length} records and returned them to the queue`;
  }

  // Reuses each box's own remove method for dedupe, surtitle removal and the derived
  // sector/asset-class recalc.
  // NOTE: PM-153: returns true for every boxed pair even when the per-box remove finds nothing,
  // so the caller still revokes the decision when a record left its box some other way (its own
  // delete chip, or a sibling suggestion sharing the same record) — otherwise Unlink is stranded
  // forever on a card with nothing left to undo. False only for a box-less pair, which never had
  // a record to lose.
  private removeSmartLinkedRecord(item: ISmartLink): boolean {
    const box = SMART_LINK_TARGETS[smartLinkTargetKey(item)]?.box;
    if (!box) {
      return false;
    }
    const listItem: ISelectListItem = {
      id: item.record_id,
      displayName: item.display_name,
    };
    switch (box) {
      case 'manager': {
        // removeManager needs the stored IManager for the surtitle payload's name.
        const entry = this.managerListControl.value.find(
          (candidate) =>
            String(
              candidate[RELATED_MANAGER_FORM_PATHS.SELECTED_MANAGER()]?.id
            ) === String(item.record_id)
        );
        if (entry) {
          // The caller (onUnlinkSuggestion / undoBulkAccept) already revokes the decision
          // itself right after this returns, so the box-level removal must not revoke it again.
          this.removeManager(
            entry[RELATED_MANAGER_FORM_PATHS.SELECTED_MANAGER()],
            false
          );
        }
        break;
      }
      case 'fund':
        this.removeFund(listItem, false);
        break;
      case 'sector':
        this.removeSector(listItem, false);
        break;
      case 'assetClass':
        this.removeAssetClass(listItem, false);
        break;
      case 'investmentTrust':
        this.removeInvestmentTrust(listItem, false);
        break;
      case 'privateMarketsFund':
        this.removePrivateMarketsFund(listItem, false);
        break;
    }
    this.smartLinkedKeys.delete(`${box}:${item.record_id}`);
    if (box === 'manager') {
      this.smartLinkedManagerIds = this.smartLinkedManagerIds.filter(
        (id) => id !== item.record_id
      );
    }
    return true;
  }

  // The undo-accept counterpart to removeSmartLinkedRecord: writes the record back into its box
  // without touching the decision, since the card's own ACCEPT state was already restored by the
  // state service before this fires. Never routes through acceptSuggestion, which would re-POST
  // a decision the server never let go of.
  private restoreSmartLinkedRecord(item: ISmartLink): void {
    const box = SMART_LINK_TARGETS[smartLinkTargetKey(item)]?.box;
    if (!box || this.isSmartLinked(box, item.record_id)) {
      return;
    }
    this.addSmartLinkedKey(box, item.record_id);
    this.addSmartLinkToBox(box, item)
      .pipe(takeUntil(this.destroy$))
      .subscribe((wasAdded) => {
        if (!wasAdded) {
          this.removeSmartLinkedKey(box, item.record_id);
        }
      });
  }

  private buildAcceptScopes(
    strong: ISmartLink[],
    strongAndPossible: ISmartLink[],
    weak: ISmartLink[]
  ): ISmartLinkAcceptScope[] {
    const strongScope = this.acceptableItems(strong);
    const midScope = this.acceptableItems(strongAndPossible);
    const allScope = this.acceptableItems([...strongAndPossible, ...weak]);
    const scopes: ISmartLinkAcceptScope[] = [];
    if (strongScope.length > 0) {
      scopes.push({
        key: 'strong',
        label: `Strong (${strongScope.length})`,
        items: strongScope,
      });
    }
    // Equal membership would put the same click in the menu twice.
    if (midScope.length > strongScope.length) {
      scopes.push({
        key: 'strongPossible',
        label: `Strong + Possible (${midScope.length})`,
        items: midScope,
      });
    }
    if (allScope.length > midScope.length) {
      scopes.push({
        key: 'all',
        label: `Include weak (${allScope.length})`,
        items: allScope,
      });
    }
    return scopes;
  }

  private acceptableItems(items: ISmartLink[]): ISmartLink[] {
    return items.filter(
      (item) =>
        !this.isDeciding(item.smart_link_id) &&
        !this.alreadyTaggedSmartLinkIds.has(item.smart_link_id)
    );
  }

  private get unambiguousWeakSmartLinks(): ISmartLink[] {
    return this.unambiguousWithin(this.weakSmartLinks, [
      ...this.pendingSmartLinks,
      ...this.weakSmartLinks,
    ]);
  }

  // Ambiguity is judged against the undecided set, so resolving one sibling releases the other
  // back into the bulk scopes rather than stranding it.
  private unambiguousWithin(
    items: ISmartLink[],
    within: ISmartLink[]
  ): ISmartLink[] {
    const counts = new Map<string, number>();
    within.forEach(({ ambiguity_group }) => {
      if (ambiguity_group) {
        counts.set(ambiguity_group, (counts.get(ambiguity_group) ?? 0) + 1);
      }
    });
    return items.filter(
      (item) =>
        item.ambiguity_group === null ||
        counts.get(item.ambiguity_group) === 1
    );
  }

  // On the weak section rather than per group, because weak items are a flat set across all
  // groups. One reason covers the batch, so a reason stays mandatory either way.
  onDismissAllWeak(reason: SmartLinkRejectReason): void {
    const dismissed = this.weakSmartLinks.filter(
      (item) => !this.isDeciding(item.smart_link_id),
    );
    if (dismissed.length === 0) {
      return;
    }
    dismissed.forEach((item) =>
      this.smartLinkingState.reject(item.smart_link_id, reason),
    );
    this.decisionAnnouncement =
      dismissed.length === 1
        ? `Dismissed ${dismissed[0].display_name}`
        : `Dismissed ${dismissed.length} suggestions`;
    // dismissAllWeakBtn removes itself with the list it emptied, so focus cannot stay on it.
    this.focusSuggestionQueue();
  }

  // Null where the pair maps to no box at all. The Linked scope counts both, so each card has to
  // say which of the two it is: only a record in Related data can be taken back out.
  smartLinkBoxLabel(item: ISmartLink): string | null {
    const target = SMART_LINK_TARGETS[smartLinkTargetKey(item)];
    return target?.box ? target.label : null;
  }

  // Null where the pair has no AMR record page: the card then renders no link rather than a 404.
  smartLinkRecordUrl(item: ISmartLink): string | null {
    const infoRoute = SMART_LINK_TARGETS[smartLinkTargetKey(item)]?.infoRoute;
    return infoRoute
      ? this.appConfig.amrUrl + infoRoute + item.record_id
      : null;
  }

  // Only a bulk accept gets one: it writes records into boxes that may be off screen, so the
  // reversal has to travel with the confirmation. A dismissal keeps a Restore on every card.
  private openUndoSnackBar(message: string, undo: () => void): void {
    // A second bulk accept replaces the first snackbar, so its ref can never fire again — but
    // the subscription would outlive it until the panel is destroyed.
    this.smartLinkUndoAction?.unsubscribe();
    this.smartLinkUndoAction = this._snackBar
      .open(message, SMART_LINK_UNDO_ACTION_LABEL, {
        duration: SMART_LINK_UNDO_DURATION_MS,
        // decisionAnnouncement already speaks the accept; a second live region would double it.
        politeness: 'off',
      })
      .onAction()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => undo());
  }

  // Emits whether the accept was recorded, and what the body link did. The decision follows the
  // box write rather than racing it: recording an accept the box never took left the card
  // claiming "In Managers" with nothing behind it, and Unlink could not undo what was never added.
  private acceptSuggestion(item: ISmartLink): Observable<IAcceptOutcome> {
    const target = SMART_LINK_TARGETS[smartLinkTargetKey(item)];
    if (!target?.box) {
      // No box to write to, so the decision is the whole of it.
      this.smartLinkingState.accept(item.smart_link_id);
      return of({ wasAccepted: true, bodyLink: null });
    }
    const box = target.box;
    const key = `${box}:${item.record_id}`;
    // A sibling smart link — a double-click, or another item in the same bulk accept — already
    // has this record's write in flight: join it rather than repeat it. Checked ahead of
    // isSmartLinked, which the pending write's own key-add already made true.
    const pendingWrite = this.pendingBoxWrites.get(key);
    if (pendingWrite) {
      pendingWrite.smartLinkIds.add(item.smart_link_id);
      return pendingWrite.result$;
    }
    if (this.isSmartLinked(box, item.record_id)) {
      this.smartLinkingState.accept(item.smart_link_id);
      // Still offered the body link: the box write is a no-op, but an ambiguity sibling
      // resolving to the same record has not been given one, and the insert answers
      // "already linked" for itself rather than assuming from the box.
      return of({ wasAccepted: true, bodyLink: this.autoLinkFundInBody(item) });
    }
    // Key first: pushing to the FormArray rebuilds the display list synchronously, and that
    // rebuild must already see the provenance key.
    this.addSmartLinkedKey(box, item.record_id);
    const smartLinkIds = new Set([item.smart_link_id]);
    const result$ = this.addSmartLinkToBox(box, item).pipe(
      map((wasAdded) => {
        if (!wasAdded) {
          this.removeSmartLinkedKey(box, item.record_id);
          return { wasAccepted: false, bodyLink: null };
        }
        smartLinkIds.forEach((id) => this.smartLinkingState.accept(id));
        // NOTE: With the box write, not after the decision. accept() returns void, so there is
        // no success signal to wait for - a failure arrives later on decisionError$, whose
        // existing compensation already strips the link (over-strips it: PM-164).
        return { wasAccepted: true, bodyLink: this.autoLinkFundInBody(item) };
      }),
      // shareReplay holds the source open once every caller has unsubscribed, so teardown has to
      // be explicit: without it a destroy mid-write still lands the accept, on a form nobody saves.
      takeUntil(this.destroy$),
      finalize(() => this.pendingBoxWrites.delete(key)),
      shareReplay(1)
    );
    this.pendingBoxWrites.set(key, { result$, smartLinkIds });
    return result$;
  }

  /**
   * Inserts one body link for an accepted Private Markets fund, at a mention the run cited.
   *
   * NOTE: A carve-out for Private Markets funds, not a per-type hook (ADR-0006). Every other
   * suggestion type stays tag-only, so this returns null for them and the message says nothing
   * about the body.
   */
  private autoLinkFundInBody(
    item: ISmartLink
  ): PrivateMarketsBodyLinkResult | null {
    const box = SMART_LINK_TARGETS[smartLinkTargetKey(item)]?.box;
    if (box !== 'privateMarketsFund') {
      return null;
    }

    // NOTE: Body-field evidence only. A headline or standfirst match is a guard rail, not a
    // suppression - it simply is not something this feature is allowed to link, which is how
    // PM-137's "do not add if the match is in the title" falls out with no special case.
    const clauses = item.evidence
      .filter((entry) => entry.field === 'body')
      .map((entry) => entry.matched_text);
    if (clauses.length === 0) {
      return 'no-body-evidence';
    }

    const attempt: IPrivateMarketsFundBodyLinkAttempt = {
      request: {
        id: item.record_id,
        name: item.display_name,
        // NOTE: the same rule the manual "+" follows, so the accept path can never emit a format the
        // affordance two inches away is refusing to. The gate is a per-user allowlist (PM-160), so
        // authors outside it emit regular links until it is removed (ADR-0002, ADR-0006).
        format: this.isBodyLinkFormatsEnabled
          ? BodyLinkFormat.Hovercard
          : undefined,
        clauses,
      },
      result: 'unavailable',
    };
    this.handleAddPrivateMarketsFundBodyLink.emit(attempt);
    return attempt.result;
  }

  // NOTE: 'unavailable' claims nothing about mentions: the editor could not be reached, so only
  // the missing link is true (ADR-0009).
  private bodyLinkSuffix(result: PrivateMarketsBodyLinkResult | null): string {
    if (result === 'already-linked') {
      return SMART_LINK_BODY_ALREADY_PRESENT_SUFFIX;
    }
    if (result === 'no-body-evidence') {
      return SMART_LINK_BODY_NO_EVIDENCE_SUFFIX;
    }
    if (result === 'not-found') {
      return SMART_LINK_BODY_NAME_NOT_FOUND_SUFFIX;
    }
    if (result === 'unavailable') {
      return SMART_LINK_BODY_UNAVAILABLE_SUFFIX;
    }
    return '';
  }

  // NOTE: 'already-linked' is a success, so it is spoken but never shown (ADR-0009).
  private isReportedBodyMiss(
    result: PrivateMarketsBodyLinkResult | null
  ): boolean {
    return (
      result === 'no-body-evidence' ||
      result === 'not-found' ||
      result === 'unavailable'
    );
  }

  private bulkAcceptAnnouncement(
    added: number,
    linked: number,
    missed: number
  ): string {
    const parts = [`Added ${added} records to Related data`];
    if (linked > 0) {
      parts.push(`${linked} given a link in the article body`);
    }
    if (missed > 0) {
      parts.push(
        `no link added to the article body for ${missed} Private Markets ${
          missed === 1 ? 'fund' : 'funds'
        }`
      );
    }
    return parts.join(', ');
  }

  // NOTE: forkJoin preserves the order of the array it is given, and a Private Markets fund's
  // accept chain is synchronous, so this is the order body links get claimed in. A shorter name
  // going first claims a prefix of a longer one - emitting "<a>Blackstone Real Estate</a> Income
  // Trust", which points at the wrong fund and blocks the right one - so the longest name has to
  // go first. Only PM funds are reordered, and only against each other: they are written back
  // into the slots they already held, so nothing else in the batch moves.
  private longestFundNamesFirst(items: ISmartLink[]): ISmartLink[] {
    const slots = items
      .map((item, index) => ({ item, index }))
      .filter(
        ({ item }) =>
          SMART_LINK_TARGETS[smartLinkTargetKey(item)]?.box ===
          'privateMarketsFund'
      )
      .map(({ index }) => index);
    if (slots.length < 2) {
      return items;
    }
    const byNameLength = slots
      .map((index) => items[index])
      .sort((a, b) => b.display_name.length - a.display_name.length);
    const ordered = [...items];
    slots.forEach((index, at) => (ordered[index] = byNameLength[at]));
    return ordered;
  }

  private buildScopeOptions(): ISmartLinkQueueScopeOption[] {
    return [
      {
        key: 'review',
        label: 'to review',
        // The same field the badge and the Suggested tab read, so the header cannot claim less
        // outstanding work than this scope opens.
        count: this.smartLinkPendingCount,
      },
      { key: 'linked', label: 'linked', count: this.smartLinkLinkedCount },
      {
        key: 'dismissed',
        label: 'dismissed',
        count: this.smartLinkDismissedCount,
      },
    ];
  }

  // Every scope opens, including an empty one, which states its own emptiness rather than
  // refusing the click.
  selectSmartLinkScope(scope: SmartLinkQueueScope): void {
    if (scope === this.smartLinkScope) {
      return;
    }
    this.smartLinkScope = scope;
    // The queue is a bounded scroller that keeps its offset across the swap, so a switch made
    // while scrolled would land mid-list.
    if (this.suggestionQueue) {
      this.suggestionQueue.nativeElement.scrollTop = 0;
    }
  }

  // NOTE: Straight through: the card knows which row was clicked, the form above owns the flash.
  onFlashMention(targets: ISmartLinkMentionFlashTarget[]): void {
    this.handleFlashMention.emit(targets);
  }

  // The record must go first: clearing the decision while it is still in a box would leave one
  // record both linked and pending.
  onUnlinkSuggestion(smartLinkId: string): void {
    const item = this.linkedSmartLinks.find(
      (candidate) => candidate.smart_link_id === smartLinkId,
    );
    if (!item) {
      return;
    }
    const neighbourId = this.neighbourCardId(smartLinkId);
    if (!this.removeSmartLinkedRecord(item)) {
      return;
    }
    this.smartLinkingState.undoAccept(smartLinkId);
    this.decisionAnnouncement = `Unlinked ${item.display_name} and returned it to the queue`;
    this.focusAfterDecision(neighbourId);
  }

  isDeciding(smartLinkId: string): boolean {
    return this.decisionsInFlight.includes(smartLinkId);
  }

  alternativeNames(item: ISmartLink): string[] {
    return ambiguityAlternativeNames(item, this.allSmartLinks);
  }

  // The groups are built in a template expression, so the loop is handed new arrays on every
  // change-detection pass. See trackCandidate on the card for what unkeyed costs the mouse.
  trackSuggestionGroup(_index: number, group: ISmartLink[]): string {
    return group[0]?.smart_link_id ?? '';
  }

  // The route and the box are panel knowledge, so the card is handed records that carry both.
  smartLinkCandidates(items: ISmartLink[]): ISmartLinkCandidate[] {
    return items.map((item) => ({
      item,
      recordUrl: this.smartLinkRecordUrl(item),
      boxLabel: this.smartLinkBoxLabel(item),
      alreadyTagged: this.isAlreadyTaggedSuggestion(item),
    }));
  }

  // Scopes that hold decided or weak records show one per card: a choice between readings only
  // exists while both are still pending, which is the review scope's blocks.
  smartLinkSingletons(items: ISmartLink[]): ISmartLink[][] {
    return items.map((item) => [item]);
  }

  private initSmartLinking(): void {
    this.smartLinkTriggers.register(
      SMART_LINK_TRIGGER_ID,
      this.smartLinkTrigger
    );

    this.smartLinkingState.state$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => this.deriveSmartLinkViewState(state));

    this.smartLinkingState.decisionError$
      .pipe(takeUntil(this.destroy$))
      .subscribe((failure) => {
        if (failure.action === SmartLinkDecisionAction.ACCEPT) {
          const item = this.allSmartLinks.find(
            (candidate) => candidate.smart_link_id === failure.smartLinkId
          );
          if (item) {
            if (failure.phase === 'decide') {
              // An accept writes its record before the POST, so a failed or already-decided
              // accept leaves that record in its box with no matching decision behind it.
              this.removeSmartLinkedRecord(item);
            } else {
              // Undo already withdrew the record before asking the server to revoke, so a
              // refused or failed undo has to put it back — the card just went back to ACCEPT.
              this.restoreSmartLinkedRecord(item);
            }
          }
        }
        this._snackBar.open(
          this.smartLinkDecisionErrorMessage(failure.reason),
          null,
          { duration: 5000 }
        );
      });
  }

  // 'already_decided' and 'not_revoked' are both deterministic — the suggestion already holds
  // a decision, or something else holds the slot — so telling the editor to "try again" would
  // be misleading; only 'unavailable' names a retry-worthy failure.
  private smartLinkDecisionErrorMessage(
    reason: ISmartLinkDecisionFailure['reason']
  ): string {
    switch (reason) {
      case 'already_decided':
        return SMART_LINK_ALREADY_DECIDED_MESSAGE;
      case 'not_revoked':
        return SMART_LINK_NOT_REVOKED_MESSAGE;
      default:
        return SMART_LINK_UNAVAILABLE_MESSAGE;
    }
  }

  private deriveSmartLinkViewState(state: ISmartLinkRunState): void {
    const previousStatus = this.smartLinkStatus;
    this.smartLinkStatus = state.status;
    this.smartLinkErrorReason = state.errorReason;
    const rawPending = pendingCards(state);
    const rawWeak = weakPendingCards(state);
    // NOTE: PM-165: a suggestion whose target says an already-boxed record should never
    // resurface (every type except Private Markets Fund — see smart-link-targets.ts) drops out
    // of the queue, and out of every count derived from it, rather than sitting there looking new.
    const hiddenSmartLinkIds = new Set(
      [...rawPending, ...rawWeak]
        .filter((item) => this.isHiddenDuplicate(item))
        .map((item) => item.smart_link_id)
    );
    this.pendingSmartLinks = rawPending.filter(
      (item) => !hiddenSmartLinkIds.has(item.smart_link_id)
    );
    this.weakSmartLinks = rawWeak.filter(
      (item) => !hiddenSmartLinkIds.has(item.smart_link_id)
    );
    this.dismissedSmartLinks = dismissedCards(state);
    this.linkedSmartLinks = linkedItems(state);
    // NOTE: hidden duplicates dropped here too: they will never receive a decision, so counting
    // them would leave the reviewed-progress bar permanently short and "All N reviewed"
    // overstating N.
    this.allSmartLinks = state.smartLinks.filter(
      (link) => !hiddenSmartLinkIds.has(link.smart_link_id)
    );
    // Before the groups: their accept scopes exclude decisions already in flight.
    this.decisionsInFlight = state.decisionsInFlight;
    const undecidedSmartLinks = [
      ...this.pendingSmartLinks,
      ...this.weakSmartLinks,
    ];
    // NOTE: a Private Markets Fund suggestion for a record already in its box stays in the
    // queue — accepting it can still add a body link (ADR-0006) — but should not read as new
    // work: it is marked here, then kept out of the review count and every bulk-accept scope
    // below.
    this.alreadyTaggedSmartLinkIds = new Set(
      undecidedSmartLinks
        .filter((item) => this.isAlreadyInBox(item))
        .map((item) => item.smart_link_id)
    );
    const reviewableSmartLinks = undecidedSmartLinks.filter(
      (item) => !this.alreadyTaggedSmartLinkIds.has(item.smart_link_id)
    );
    this.suggestionGroups = this.buildSuggestionGroups(
      this.pendingSmartLinks,
      undecidedSmartLinks
    );
    this.pendingGroupCounts = this.buildPendingGroupCounts(reviewableSmartLinks);
    this.queueAcceptScopes = this.buildAcceptScopes(
      this.suggestionGroups.flatMap((group) => group.strongItems),
      this.suggestionGroups.flatMap((group) => group.strongAndPossibleItems),
      this.unambiguousWeakSmartLinks
    );
    this.queueAmbiguousCount =
      undecidedSmartLinks.length -
      (this.suggestionGroups.reduce(
        (total, group) => total + group.strongAndPossibleItems.length,
        0
      ) +
        this.unambiguousWeakSmartLinks.length);
    // Weak matches included: excluding them left the header reporting fewer suggestions than the
    // queue underneath it held. Already-tagged Private Markets funds excluded: see above.
    this.smartLinkPendingCount = reviewableSmartLinks.length;
    this.smartLinkDismissedCount = this.dismissedSmartLinks.length;
    // Box-less accepts included, because the count labels a scope: one that skipped a card it
    // opens would leave that suggestion reachable from nowhere.
    this.smartLinkLinkedCount = this.linkedSmartLinks.length;
    this.smartLinkReviewedCount = this.allSmartLinks.filter(
      (link) => !!link.decision_action
    ).length;
    this.isSmartLinkEmptyResult =
      state.status === 'complete' && this.allSmartLinks.length === 0;
    // Distinct from isSmartLinkEmptyResult, which is the run finding nothing: this is every
    // suggestion it did find having been decided.
    this.isSmartLinkAllReviewed =
      state.status === 'complete' &&
      this.allSmartLinks.length > 0 &&
      this.pendingSmartLinks.length === 0 &&
      this.weakSmartLinks.length === 0;
    this.smartLinkScopeOptions = this.buildScopeOptions();
    if (state.status !== 'complete') {
      this.smartLinkScope = 'review';
    }
    this.syncSmartLinkBusyClock(previousStatus, state.status);
    this.handleUserRunTransition(previousStatus, state.status);
  }

  private isBusySmartLinkStatus(status: SmartLinkRunStatus): boolean {
    return status === 'triggering' || status === 'running';
  }

  // The content is read on the transition into 'triggering', where postRun() has patched the
  // status and then synchronously reads gateway.getContent() for the body — so this observes
  // exactly what is sent. The wait clock starts at the first 'running' emission, the instant
  // startPolling() arms its deadline timer: measuring from the click would let the 'triggering'
  // round trip claim a countdown that has not started.
  private syncSmartLinkBusyClock(
    previous: SmartLinkRunStatus,
    current: SmartLinkRunStatus
  ): void {
    if (
      current === 'triggering' &&
      previous !== 'triggering' &&
      this.userRunInFlight
    ) {
      if (!this.smartLinkGateway) {
        this.smartLinkScanContent = 'restored';
        this.smartLinkScanSnapshot = null;
      } else {
        this.smartLinkScanSnapshot = this.smartLinkGateway.getContent();
        this.smartLinkScanContent = hasScannableText(this.smartLinkScanSnapshot)
          ? 'text'
          : 'empty';
      }
    }
    if (!this.isBusySmartLinkStatus(current)) {
      this.stopSmartLinkBusyTicker();
      this.smartLinkScanContent = 'restored';
      return;
    }
    if (current === 'running' && this.smartLinkPollStartedAt === null) {
      this.startSmartLinkBusyTicker();
    }
  }

  private startSmartLinkBusyTicker(): void {
    this.stopSmartLinkBusyTicker();
    this.smartLinkPollStartedAt = Date.now();
    this.smartLinkWaitSeconds = 0;
    this.smartLinkBusyTicker = timer(
      SMART_LINK_BUSY_TICK_MS,
      SMART_LINK_BUSY_TICK_MS
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateSmartLinkWait());
  }

  private stopSmartLinkBusyTicker(): void {
    this.smartLinkBusyTicker?.unsubscribe();
    this.smartLinkBusyTicker = null;
    this.smartLinkPollStartedAt = null;
    this.smartLinkWaitSeconds = 0;
  }

  // Recomputed from Date.now() rather than counted: a backgrounded tab throttles timers to roughly
  // one a minute, so a tick counter would under-report the wait for the user who walked away.
  private updateSmartLinkWait(): void {
    this.smartLinkWaitSeconds =
      this.smartLinkPollStartedAt === null
        ? 0
        : Math.floor((Date.now() - this.smartLinkPollStartedAt) / 1000);
  }

  // Completion never forces a view switch: the user is already on Suggested from the trigger, or
  // deliberately navigated away.
  private handleUserRunTransition(
    previousStatus: SmartLinkRunStatus,
    status: SmartLinkRunStatus
  ): void {
    if (!this.userRunInFlight || previousStatus === status) {
      return;
    }
    if (status === 'complete') {
      this.userRunInFlight = false;
      return;
    }
    if (status === 'error') {
      // A snackbar as well as the error row, so the failure is visible while the panel is
      // collapsed or the Selected view is active.
      this._snackBar.open(this.smartLinkErrorMessage, null, {
        duration: 5000,
      });
      this.userRunInFlight = false;
      return;
    }
    if (status === 'idle') {
      // A cancelled or failed pre-run save aborts the run silently, so leave the user where they
      // were rather than on an empty Suggested view.
      if (this.activeView === 'suggested') {
        this.activeView = 'selected';
      }
      this.userRunInFlight = false;
    }
  }

  // Shared by the review queue's groups and the collapsed preview's breakdown, which group the
  // same records by the same key over different subsets.
  private groupByTarget(items: ISmartLink[]): ISmartLinkTargetGroup[] {
    const unmappedOrder = Object.keys(SMART_LINK_TARGETS).length;
    const grouped = new Map<string, ISmartLinkTargetGroup>();
    items.forEach((item) => {
      const key = smartLinkTargetKey(item);
      const existing = grouped.get(key);
      if (existing) {
        existing.items.push(item);
        return;
      }
      // Unknown pairs still get a decision-only group, so contract drift surfaces instead of
      // suggestions being silently dropped.
      const target = SMART_LINK_TARGETS[key] ?? {
        label: entityTypeLabel(item),
        box: null,
        order: unmappedOrder,
        hideWhenAlreadyLinked: false,
      };
      grouped.set(key, { target, items: [item] });
    });

    return Array.from(grouped.values()).sort(
      (a, b) => a.target.order - b.target.order
    );
  }

  private buildPendingGroupCounts(
    undecidedItems: ISmartLink[]
  ): ISmartLinkGroupCount[] {
    return this.groupByTarget(undecidedItems).map(({ target, items }) => ({
      label: target.label,
      count: items.length,
    }));
  }

  private buildSuggestionGroups(
    pendingItems: ISmartLink[],
    undecidedItems: ISmartLink[]
  ): ISmartLinkSuggestionGroup[] {
    // Ambiguity siblings stay in the list to be decided individually but never qualify for a bulk
    // accept, which would land both in the box. Judged against the whole undecided set, not just
    // this group: a rival reading below the weak threshold is still a rival reading.
    return this.groupByTarget(pendingItems).map(({ target, items }) => {
      const sortedItems = [...items].sort(
        (a, b) => b.confidence - a.confidence || a.rank - b.rank
      );
      const unambiguousItems = this.unambiguousWithin(
        sortedItems,
        undecidedItems
      );
      const strongItems = unambiguousItems.filter(
        (item) => confidenceTier(item.confidence) === 'Strong'
      );
      return {
        label: target.label,
        items: sortedItems,
        blocks: this.buildMentionBlocks(sortedItems),
        strongItems,
        strongAndPossibleItems: unambiguousItems,
        ambiguousCount: sortedItems.length - unambiguousItems.length,
        acceptScopes: this.buildAcceptScopes(strongItems, unambiguousItems, []),
      };
    });
  }

  // A block keeps the position of its strongest member, so the confidence ordering the group was
  // sorted into still holds.
  private buildMentionBlocks(
    sortedItems: ISmartLink[]
  ): ISmartLinkMentionBlock[] {
    const blocks: ISmartLinkMentionBlock[] = [];
    const byAmbiguityGroup = new Map<string, ISmartLinkMentionBlock>();

    sortedItems.forEach((item) => {
      const existing = item.ambiguity_group
        ? byAmbiguityGroup.get(item.ambiguity_group)
        : undefined;
      if (existing) {
        existing.items.push(item);
        return;
      }
      const block: ISmartLinkMentionBlock = { items: [item] };
      if (item.ambiguity_group) {
        byAmbiguityGroup.set(item.ambiguity_group, block);
      }
      blocks.push(block);
    });

    return blocks;
  }

  // The collapsed preview renders the raw form control values rather than the *DisplayList arrays,
  // so it has no isSmartLinked flag to read and asks here instead.
  isSmartLinked(box: SmartLinkTargetBox, recordId: string | number): boolean {
    return this.smartLinkedKeys.has(`${box}:${recordId}`);
  }

  // NOTE: PM-165: whether a suggestion's own record is already present in the box its target
  // writes to — regardless of whether Smart Link (this session or an earlier one) or a manual
  // add put it there. Distinct from isSmartLinked, which only ever answers for Smart Link's own
  // writes.
  private isAlreadyInBox(item: ISmartLink): boolean {
    const box = SMART_LINK_TARGETS[smartLinkTargetKey(item)]?.box;
    return !!box && this.boxRecordIds(box).has(item.record_id);
  }

  // NOTE: hideWhenAlreadyLinked is false only for Private Markets Fund (smart-link-targets.ts):
  // accepting an already-tagged fund can still add a body link (ADR-0006), so that suggestion
  // stays in the queue and is marked instead of hidden — see alreadyTaggedSmartLinkIds.
  private isHiddenDuplicate(item: ISmartLink): boolean {
    const target = SMART_LINK_TARGETS[smartLinkTargetKey(item)];
    return !!target?.hideWhenAlreadyLinked && this.isAlreadyInBox(item);
  }

  // NOTE: ids are normalised to strings: record_id is always a string on the wire, but five of
  // the six boxes parseInt their id on add (addFund, addSector, addAssetClass,
  // addInvestmentTrust, the manager lookup) — a strict comparison against the raw stored value
  // would never match them.
  private boxRecordIds(box: SmartLinkTargetBox): Set<string> {
    // NOTE: the manager FormArray's raw value shape — keyed by RELATED_MANAGER_FORM_PATHS'
    // SELECTED_MANAGER field rather than a literal, so the type can never drift from the key
    // _generateManagerFormGroup actually builds.
    type IManagerFormGroupValue = {
      [K in ReturnType<
        typeof RELATED_MANAGER_FORM_PATHS.SELECTED_MANAGER
      >]?: IManager;
    };
    switch (box) {
      case 'manager':
        return new Set(
          this.managerListControl.value.map((entry: IManagerFormGroupValue) =>
            String(entry[RELATED_MANAGER_FORM_PATHS.SELECTED_MANAGER()]?.id)
          )
        );
      case 'fund':
        return new Set(
          this.fundListControl.value.map((f: Fund) => String(f.id))
        );
      case 'sector':
        return new Set(
          this.sectorListControl.value.map((s: ISector) => String(s.id))
        );
      case 'assetClass':
        return new Set(
          this.assetClassListControl.value.map((a: IAssetClass) =>
            String(a.id)
          )
        );
      case 'investmentTrust':
        return new Set(
          this.investmentTrustListControl.value.map((i: InvestmentTrust) =>
            String(i.id)
          )
        );
      case 'privateMarketsFund':
        return new Set(
          this.privateMarketsFundListControl.value.map(
            (p: PrivateMarketsFund) => String(p.id)
          )
        );
    }
  }

  // NOTE: consumed by the suggestion card to show an "already tagged" marker instead of hiding
  // the card.
  isAlreadyTaggedSuggestion(item: ISmartLink): boolean {
    return this.alreadyTaggedSmartLinkIds.has(item.smart_link_id);
  }

  private addSmartLinkedKey(box: SmartLinkTargetBox, recordId: string): void {
    this.smartLinkedKeys.add(`${box}:${recordId}`);
    if (box === 'manager') {
      this.smartLinkedManagerIds = [...this.smartLinkedManagerIds, recordId];
    }
  }

  // Rolls back addSmartLinkedKey when the box write it was staged for did not land.
  private removeSmartLinkedKey(box: SmartLinkTargetBox, recordId: string): void {
    this.smartLinkedKeys.delete(`${box}:${recordId}`);
    if (box === 'manager') {
      this.smartLinkedManagerIds = this.smartLinkedManagerIds.filter(
        (id) => id !== recordId
      );
    }
  }

  // A box's own delete chip removes the record from Related data without knowing anything about
  // the smart-link decision behind it; without this the Linked queue keeps showing an accept the
  // box no longer backs, and the record only disappears from Linked on a second, explicit Unlink.
  // Filters rather than finds: PM-153's bulk-accept dedupe means more than one smart_link_id can
  // carry an ACCEPT decision for the same record, and all of them must be revoked together.
  private revokeLinkedSmartLink(
    box: SmartLinkTargetBox,
    recordId: string | number
  ): void {
    if (!this.isSmartLinked(box, recordId)) {
      return;
    }
    this.removeSmartLinkedKey(box, String(recordId));
    this.linkedSmartLinks
      .filter(
        (candidate) =>
          SMART_LINK_TARGETS[smartLinkTargetKey(candidate)]?.box === box &&
          String(candidate.record_id) === String(recordId)
      )
      .forEach((item) => this.smartLinkingState.undoAccept(item.smart_link_id));
  }

  private markSmartLinked(
    items: ISelectListItem[],
    box: SmartLinkTargetBox
  ): ISelectListItem[] {
    items.forEach((item) => {
      item.isSmartLinked = this.smartLinkedKeys.has(`${box}:${item.id}`);
    });
    return items;
  }

  // NOTE: PM-149: seeding from the persisted addedViaSmartLink is what lets the icon survive a
  // reload, when smartLinkedKeys — otherwise session-only state — starts out empty again. The
  // isSmartLinked guard keeps re-seeding a no-op for funds already seeded, which matters because
  // addSmartLinkedKey's array append for the manager box is not idempotent like Set.add.
  private seedPrivateMarketsFundSmartLinkedKeys(
    funds: PrivateMarketsFund[]
  ): void {
    funds
      .filter(
        (fund) =>
          fund.addedViaSmartLink &&
          !this.isSmartLinked('privateMarketsFund', fund.id)
      )
      .forEach((fund) => this.addSmartLinkedKey('privateMarketsFund', fund.id));
  }

  // Emits whether the record ended up in the box: only the manager path actually defers, and a
  // throw from any of them becomes false rather than escaping into the caller's loop.
  private addSmartLinkToBox(
    box: SmartLinkTargetBox,
    item: ISmartLink
  ): Observable<boolean> {
    const listItem: ISelectListItem = {
      id: item.record_id,
      displayName: item.display_name,
    };
    return defer(() => {
      switch (box) {
        case 'manager':
          return this.addManager$(listItem);
        case 'fund':
          this.addFund(listItem);
          return of(true);
        case 'sector':
          this.addSector(listItem);
          return of(true);
        case 'assetClass':
          this.addAssetClass(listItem);
          return of(true);
        case 'investmentTrust':
          this.addInvestmentTrust(listItem);
          return of(true);
        case 'privateMarketsFund':
          this.addPrivateMarketsFund(listItem, true);
          return of(true);
      }
    }).pipe(catchError(() => of(false)));
  }
}
